import type {
    PredefineMode,
    PredefineValueEntry,
    Rule,
    RuleCondition,
    RuleConditionLeaf,
    RuleGroup,
    RuleWhenExecute,
    ValidationType,
} from '../schema/rule';
import { intFromMixed, localized, optionalString } from './coerce';

export function parseRuleArray(raw: unknown): Rule[] {
    if (!Array.isArray(raw)) return [];
    return raw.map(parseRule);
}

export function parseRuleGroupArray(raw: unknown): RuleGroup[] {
    if (!Array.isArray(raw)) return [];
    return raw.map(parseRuleGroup);
}

function parseRule(raw: unknown): Rule {
    const r = (raw ?? {}) as Record<string, unknown>;

    // error_message in top-level rules is i18n object; in rulesgroups it's a plain string.
    let errorMessage: Rule['errorMessage'];
    if (r.error_message == null) {
        errorMessage = undefined;
    } else if (typeof r.error_message === 'string') {
        errorMessage = r.error_message;
    } else {
        errorMessage = localized(r.error_message);
    }

    return {
        id: intFromMixed(r.id, 0),
        sortOrder: intFromMixed(r.sort_order, 0),
        weight: intFromMixed(r.weight, 0),
        // Legacy wire format uses `rule_item`; some newer paths use `rule`.
        // Accept either to stay tolerant of both shapes.
        rule: parseRuleCondition(r.rule_item ?? r.rule),
        validationType: normaliseValidationType(r.validation_type),
        blacklistCharacteristics: stringArray(r.blacklist_options),
        blacklistValues: stringArray(r.blacklist_choices),
        predefineValues: parsePredefineValues(r.predefine_values),
        errorMessage,
        errorScope: optionalString(r.error_scope),
        errorCharacteristic: optionalString(r.error_option),
        errorOperator: optionalString(r.error_operator),
        errorValue: r.error_value,
        errorWhenExecute: normaliseWhenExecute(r.error_when_execute),
        errorType: r.error_error_type === 'warning' ? 'warning' : 'error',
        whenExecute: normaliseWhenExecute(r.when_execute),
        scope: optionalString(r.scope),
    };
}

function parsePredefineValues(raw: unknown): Rule['predefineValues'] {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const out: Record<string, PredefineValueEntry> = {};
    for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
        if (val == null || typeof val !== 'object') continue;
        const entry = val as Record<string, unknown>;
        const condition = normalisePredefineMode(entry.condition);
        out[key] = {
            condition,
            // Modes '2' and '3' carry a target Value too — they differ from
            // '1' only in *when* it applies, so the value has to survive the
            // parse. Only mode '0' (Disabled) has nothing to say.
            value: condition === '0' ? undefined : optionalString(entry.value),
            dynamicValue: entry.dynamicValue === true || entry.dynamicValue === '1' || entry.dynamicValue === 1,
        };
    }
    return out;
}

/**
 * The wire writes the per-Characteristic predefine mode as either a number or
 * a string (`0`, `'0'`, … `'3'`) depending on which admin generation saved it.
 * Anything outside 0-3 falls back to `'0'` — a no-op — rather than guessing.
 */
function normalisePredefineMode(raw: unknown): PredefineMode {
    const asString = typeof raw === 'number' ? String(raw) : raw;
    if (asString === '1' || asString === '2' || asString === '3') return asString;
    return '0';
}

function parseRuleGroup(raw: unknown): RuleGroup {
    const r = (raw ?? {}) as Record<string, unknown>;
    return {
        id: intFromMixed(r.id, 0),
        title: typeof r.title === 'string' ? r.title : '',
        sortOrder: intFromMixed(r.sort_order, 0),
        rules: parseRuleArray(r.rules),
    };
}

function parseRuleCondition(raw: unknown): RuleCondition | undefined {
    if (raw == null || typeof raw !== 'object') return undefined;
    const r = raw as Record<string, unknown>;
    const cond = r.condition === 'OR' ? 'OR' : 'AND';
    const rules = Array.isArray(r.rules) ? r.rules.map(parseRuleLeaf) : [];
    return { condition: cond, rules };
}

function parseRuleLeaf(raw: unknown): RuleConditionLeaf {
    const r = (raw ?? {}) as Record<string, unknown>;
    const leaf: RuleConditionLeaf = {
        id: typeof r.id === 'string' ? r.id : String(r.id ?? ''),
        field: typeof r.field === 'string' ? r.field : '',
        type: typeof r.type === 'string' ? r.type : 'string',
        input: typeof r.input === 'string' ? r.input : 'text',
        operator: typeof r.operator === 'string' ? r.operator : '=',
        value: r.value,
    };
    // Nested group support: a leaf can also carry `condition` + `rules`.
    if (r.condition === 'AND' || r.condition === 'OR') {
        leaf.condition = r.condition;
        if (Array.isArray(r.rules)) {
            leaf.rules = r.rules.map(parseRuleLeaf);
        }
    }
    return leaf;
}

function normaliseValidationType(v: unknown): ValidationType {
    if (v === 'error' || v === 'warning' || v === 'blacklist' || v === 'predefine_values') {
        return v;
    }
    // `predefine_qty` / `predefine_minmax` (and a missing field) land here.
    // Legacy drops such rules entirely — its switch has no default branch and
    // returns `null` (app/storage.js:920-974). Defaulting to `'error'` instead
    // showed the customer a blocking "Rule <id>" message for a rule the legacy
    // storefront had silently ignored.
    return 'unknown';
}

function normaliseWhenExecute(v: unknown): RuleWhenExecute | undefined {
    if (
        v === 'direct-before' ||
        v === 'cart' ||
        v === 'always' ||
        v === 'directly' ||
        v === 'wizard'
    ) {
        return v;
    }
    return undefined;
}

function stringArray(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    return v.filter((x): x is string | number => typeof x === 'string' || typeof x === 'number').map(String);
}
